import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { ChevronLeft, ChevronRight, Calendar as CalIcon, CalendarDays, X, AlertCircle, Filter } from "lucide-react";

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fromISO(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function startOfWeek(d) {
  const r = new Date(d);
  // Monday as first day
  const day = (r.getDay() + 6) % 7;
  r.setDate(r.getDate() - day);
  return r;
}

const DAY_NAMES = ["L", "M", "M", "J", "V", "S", "D"];
const MONTH_NAMES_RO = ["Ianuarie","Februarie","Martie","Aprilie","Mai","Iunie","Iulie","August","Septembrie","Octombrie","Noiembrie","Decembrie"];

// Status -> color
const STATUS_STYLES = {
  approved: { bg: "#16a34a", color: "#fff", label: "Aprobat" },
  pending: { bg: "#eab308", color: "#000", label: "In asteptare" },
  blocked: { bg: "#525252", color: "#fff", label: "Blocat" },
};

function inRange(iso, start, end) {
  return iso >= start && iso <= end;
}

export default function AdminCalendar() {
  const [view, setView] = useState("month"); // "month" | "week" | "quarter"
  const [anchor, setAnchor] = useState(new Date()); // anchor date inside the period shown
  const [data, setData] = useState({ cars: [], reservations: [], blocks: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selection, setSelection] = useState(null); // {car_id, date}
  const [dragRange, setDragRange] = useState(null); // {car_id, start, end}
  const [isDragging, setIsDragging] = useState(false);
  const [blockModal, setBlockModal] = useState(null); // {car_id, start_date, end_date}
  const [filter, setFilter] = useState("all"); // all | approved | pending | blocked | free
  const [carSearch, setCarSearch] = useState("");

  // Compute range
  const [startISO, endISO, days] = useMemo(() => {
    let s, e;
    if (view === "month") {
      s = startOfMonth(anchor);
      e = endOfMonth(anchor);
    } else if (view === "quarter") {
      // 3 months starting from current month
      s = startOfMonth(anchor);
      e = endOfMonth(new Date(anchor.getFullYear(), anchor.getMonth() + 2, 1));
    } else {
      s = startOfWeek(anchor);
      e = addDays(s, 6);
    }
    const arr = [];
    for (let d = new Date(s); d <= e; d = addDays(d, 1)) arr.push(toISO(d));
    return [toISO(s), toISO(e), arr];
  }, [view, anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`/admin/calendar/overview?start=${startISO}&end=${endISO}`);
      setData(r.data || { cars: [], reservations: [], blocks: [] });
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [startISO, endISO]);

  useEffect(() => { load(); }, [load]);

  // Build a quick lookup: cellState[car_id][iso] = {status: 'approved'|'pending'|'blocked', refs: [...]}
  const cellState = useMemo(() => {
    const m = {};
    const ensure = (carId, iso) => {
      if (!m[carId]) m[carId] = {};
      if (!m[carId][iso]) m[carId][iso] = { items: [] };
      return m[carId][iso];
    };
    (data.reservations || []).forEach((r) => {
      let cur = new Date(Math.max(fromISO(r.start_date), fromISO(startISO)));
      const lim = new Date(Math.min(fromISO(r.end_date), fromISO(endISO)));
      while (cur <= lim) {
        const iso = toISO(cur);
        const cell = ensure(r.car_id, iso);
        cell.items.push({ kind: "reservation", status: r.status, ref: r });
        cur = addDays(cur, 1);
      }
    });
    (data.blocks || []).forEach((b) => {
      let cur = new Date(Math.max(fromISO(b.start_date), fromISO(startISO)));
      const lim = new Date(Math.min(fromISO(b.end_date), fromISO(endISO)));
      while (cur <= lim) {
        const iso = toISO(cur);
        const cell = ensure(b.car_id, iso);
        cell.items.push({ kind: "block", ref: b });
        cur = addDays(cur, 1);
      }
    });
    return m;
  }, [data, startISO, endISO]);

  const navPrev = () => {
    if (view === "month") setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1));
    else if (view === "quarter") setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 3, 1));
    else setAnchor(addDays(anchor, -7));
  };
  const navNext = () => {
    if (view === "month") setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1));
    else if (view === "quarter") setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 3, 1));
    else setAnchor(addDays(anchor, 7));
  };
  const navToday = () => setAnchor(new Date());

  const headerLabel = useMemo(() => {
    if (view === "month") {
      return `${MONTH_NAMES_RO[anchor.getMonth()]} ${anchor.getFullYear()}`;
    }
    if (view === "quarter") {
      const s = fromISO(startISO), e = fromISO(endISO);
      return `${MONTH_NAMES_RO[s.getMonth()].slice(0,3)} → ${MONTH_NAMES_RO[e.getMonth()].slice(0,3)} ${e.getFullYear()}`;
    }
    const s = fromISO(startISO), e = fromISO(endISO);
    return `${s.getDate()} ${MONTH_NAMES_RO[s.getMonth()].slice(0,3)} → ${e.getDate()} ${MONTH_NAMES_RO[e.getMonth()].slice(0,3)} ${e.getFullYear()}`;
  }, [view, anchor, startISO, endISO]);

  const onCellMouseDown = (carId, iso) => {
    const c = cellState[carId]?.[iso];
    if (c && c.items.length > 0) {
      // Click on filled cell → show details
      setSelection({ carId, iso, items: c.items });
      return;
    }
    setIsDragging(true);
    setDragRange({ car_id: carId, start: iso, end: iso });
  };
  const onCellMouseEnter = (carId, iso) => {
    if (!isDragging || !dragRange) return;
    if (dragRange.car_id !== carId) return;
    // Allow dragging only on free cells; if hits filled, stop extending
    const c = cellState[carId]?.[iso];
    if (c && c.items.length > 0) return;
    setDragRange({ ...dragRange, end: iso });
  };
  const onMouseUp = () => {
    if (isDragging && dragRange) {
      const s = dragRange.start < dragRange.end ? dragRange.start : dragRange.end;
      const e = dragRange.start < dragRange.end ? dragRange.end : dragRange.start;
      setBlockModal({ car_id: dragRange.car_id, start_date: s, end_date: e });
    }
    setIsDragging(false);
    setDragRange(null);
  };

  useEffect(() => {
    const fn = () => onMouseUp();
    window.addEventListener("mouseup", fn);
    return () => window.removeEventListener("mouseup", fn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragRange]);

  const isCellInDrag = (carId, iso) => {
    if (!dragRange || dragRange.car_id !== carId) return false;
    const s = dragRange.start < dragRange.end ? dragRange.start : dragRange.end;
    const e = dragRange.start < dragRange.end ? dragRange.end : dragRange.start;
    return inRange(iso, s, e);
  };

  const createBlock = async (carId, start, end, reason) => {
    try {
      await api.post(`/admin/cars/${carId}/blocks`, {
        start_date: start, end_date: end, reason: reason || "Blocat din calendar",
      });
      setBlockModal(null);
      await load();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const removeBlock = async (blockId) => {
    if (!window.confirm("Stergi acest blocaj?")) return;
    // route exists in availability_routes: delete blocked interval directly
    try {
      // we need car_id for the path; find it
      const block = data.blocks.find(b => b.id === blockId);
      if (!block) return;
      await api.delete(`/admin/cars/${block.car_id}/blocks/${blockId}`);
      setSelection(null);
      await load();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const cells = useMemo(() => {
    let arr = data.cars || [];
    if (carSearch.trim()) {
      const q = carSearch.toLowerCase().trim();
      arr = arr.filter((c) => `${c.name || ""} ${c.brand || ""} ${c.model || ""}`.toLowerCase().includes(q));
    }
    if (filter !== "all") {
      arr = arr.filter((car) => {
        const state = cellState[car.id] || {};
        const allItems = Object.values(state).flatMap((c) => c.items || []);
        if (filter === "approved") return allItems.some((it) => it.kind === "reservation" && it.status === "approved");
        if (filter === "pending") return allItems.some((it) => it.kind === "reservation" && it.status === "pending");
        if (filter === "blocked") return allItems.some((it) => it.kind === "block");
        if (filter === "free") return allItems.length === 0;
        return true;
      });
    }
    return arr;
  }, [data.cars, cellState, filter, carSearch]);
  const dayCount = days.length;
  // Highlight today
  const todayISO = toISO(new Date());

  return (
    <div data-testid="admin-calendar" onMouseUp={onMouseUp}>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2" style={{ color: "var(--brand-primary)" }}>Dashboard</div>
          <h1 className="font-serif text-3xl sm:text-4xl flex items-center gap-3">
            <CalIcon className="w-7 h-7" style={{ color: "var(--brand-primary)" }} />
            Calendar Master
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center border" style={{ borderColor: "var(--brand-border)" }}>
            <button onClick={() => setView("month")} className={`px-3 py-2 text-xs uppercase tracking-widest ${view === "month" ? "bg-[var(--brand-primary)] text-black" : "btn-ghost"}`} data-testid="view-month">
              <CalendarDays className="w-3.5 h-3.5 inline mr-1" /> Lunar
            </button>
            <button onClick={() => setView("week")} className={`px-3 py-2 text-xs uppercase tracking-widest ${view === "week" ? "bg-[var(--brand-primary)] text-black" : "btn-ghost"}`} data-testid="view-week">
              <CalIcon className="w-3.5 h-3.5 inline mr-1" /> Saptamanal
            </button>
            <button onClick={() => setView("quarter")} className={`px-3 py-2 text-xs uppercase tracking-widest ${view === "quarter" ? "bg-[var(--brand-primary)] text-black" : "btn-ghost"}`} data-testid="view-quarter">
              <CalendarDays className="w-3.5 h-3.5 inline mr-1" /> Trimestrial
            </button>
          </div>
          <button onClick={navPrev} className="btn-ghost p-2" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
          <div className="font-serif text-lg sm:text-xl px-3 min-w-[180px] text-center" data-testid="cal-header-label">{headerLabel}</div>
          <button onClick={navNext} className="btn-ghost p-2" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={navToday} className="btn-ghost text-xs uppercase tracking-widest px-3" data-testid="cal-today">Azi</button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-5 text-xs">
        {Object.entries(STATUS_STYLES).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2">
            <span className="inline-block w-4 h-4" style={{ background: v.bg }} />
            <span style={{ color: "var(--brand-text-secondary)" }}>{v.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <span className="inline-block w-4 h-4 border border-dashed" style={{ borderColor: "var(--brand-primary)" }} />
          <span style={{ color: "var(--brand-text-secondary)" }}>Liber (trage pentru a bloca)</span>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 text-sm flex items-center gap-2" style={{ background: "rgba(220,38,38,0.15)", color: "#fca5a5" }}>
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Filters bar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="gantt-filters">
        <Filter className="w-3.5 h-3.5" style={{ color: "var(--brand-text-secondary)" }} />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="brand-input text-xs py-1 px-2"
          style={{ width: "auto" }}
          data-testid="gantt-filter-status"
        >
          <option value="all">Toate statusurile</option>
          <option value="approved">Doar aprobate</option>
          <option value="pending">Doar in asteptare</option>
          <option value="blocked">Doar blocate</option>
          <option value="free">Doar masini libere</option>
        </select>
        <input
          type="text"
          placeholder="Cauta masina..."
          value={carSearch}
          onChange={(e) => setCarSearch(e.target.value)}
          className="brand-input text-xs py-1 px-2"
          style={{ width: 180 }}
          data-testid="gantt-filter-car"
        />
        {(filter !== "all" || carSearch) && (
          <button onClick={() => { setFilter("all"); setCarSearch(""); }} className="btn-ghost text-xs px-2 py-1" data-testid="gantt-filter-reset">
            Reset filtre
          </button>
        )}
      </div>

      {loading && (
        <div className="text-center py-6 text-sm" style={{ color: "var(--brand-text-secondary)" }}>Se incarca...</div>
      )}

      <div className="card-surface overflow-auto" style={{ maxHeight: "70vh" }}>
        <table className="w-full text-xs select-none" style={{ borderCollapse: "collapse" }} data-testid="calendar-table">
          <thead>
            <tr>
              <th className="sticky left-0 z-20 p-2 text-left label-eyebrow whitespace-nowrap" style={{ background: "var(--brand-surface)", minWidth: 200, borderRight: "1px solid var(--brand-border)" }}>
                Masina
              </th>
              {days.map((iso) => {
                const d = fromISO(iso);
                const isToday = iso === todayISO;
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                return (
                  <th
                    key={iso}
                    className="p-1 text-center font-mono whitespace-nowrap"
                    style={{
                      minWidth: view === "quarter" ? 18 : (view === "month" ? 28 : 60),
                      background: isToday ? "rgba(212,175,55,0.15)" : isWeekend ? "rgba(255,255,255,0.02)" : "var(--brand-surface)",
                      color: isToday ? "var(--brand-primary)" : "var(--brand-text-secondary)",
                      borderBottom: "1px solid var(--brand-border)",
                    }}
                  >
                    <div className="text-[10px] opacity-70">{view === "quarter" ? (d.getDate() === 1 ? MONTH_NAMES_RO[d.getMonth()].slice(0,3) : "") : DAY_NAMES[(d.getDay() + 6) % 7]}</div>
                    <div className={view === "quarter" ? "text-[10px]" : "font-bold"}>{d.getDate()}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {cells.length === 0 && !loading && (
              <tr><td colSpan={dayCount + 1} className="text-center py-10 text-sm" style={{ color: "var(--brand-text-secondary)" }}>Nicio masina adaugata.</td></tr>
            )}
            {cells.map((car) => (
              <tr key={car.id}>
                <td
                  className="sticky left-0 z-10 p-2 whitespace-nowrap"
                  style={{
                    background: "var(--brand-surface)",
                    borderRight: "1px solid var(--brand-border)",
                    borderBottom: "1px solid var(--brand-border)",
                  }}
                  data-testid={`cal-car-${car.id}`}
                >
                  <div className="font-medium truncate max-w-[200px]">{car.name}</div>
                  <div className="text-[10px] opacity-60">{car.brand} {car.model} · {car.year}</div>
                </td>
                {days.map((iso) => {
                  const cell = cellState[car.id]?.[iso];
                  const inDrag = isCellInDrag(car.id, iso);
                  let bg = "transparent";
                  let color = "";
                  let title = "";
                  if (cell && cell.items.length) {
                    // Priority: block > approved > pending
                    const hasBlock = cell.items.find(x => x.kind === "block");
                    const hasApproved = cell.items.find(x => x.kind === "reservation" && x.status === "approved");
                    const hasPending = cell.items.find(x => x.kind === "reservation" && x.status === "pending");
                    if (hasBlock) {
                      bg = STATUS_STYLES.blocked.bg; color = STATUS_STYLES.blocked.color; title = `Blocat: ${hasBlock.ref.reason || ""}`;
                    } else if (hasApproved) {
                      bg = STATUS_STYLES.approved.bg; color = STATUS_STYLES.approved.color; title = `Aprobat: ${hasApproved.ref.guest_name || ""}`;
                    } else if (hasPending) {
                      bg = STATUS_STYLES.pending.bg; color = STATUS_STYLES.pending.color; title = `In asteptare: ${hasPending.ref.guest_name || ""}`;
                    }
                  } else if (inDrag) {
                    bg = "rgba(212,175,55,0.4)";
                  }
                  const isToday = iso === todayISO;
                  return (
                    <td
                      key={iso}
                      onMouseDown={() => onCellMouseDown(car.id, iso)}
                      onMouseEnter={() => onCellMouseEnter(car.id, iso)}
                      title={title || (cell?.items.length ? "" : "Trage pentru a bloca")}
                      data-testid={`cell-${car.id}-${iso}`}
                      style={{
                        background: bg !== "transparent" ? bg : (isToday ? "rgba(212,175,55,0.06)" : "transparent"),
                        color,
                        borderRight: "1px solid rgba(255,255,255,0.04)",
                        borderBottom: "1px solid var(--brand-border)",
                        height: 32,
                        cursor: (cell?.items.length ? "pointer" : "crosshair"),
                      }}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cell selection modal */}
      {selection && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setSelection(null)}>
          <div className="card-surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl">Detalii zi · {selection.iso}</h2>
              <button onClick={() => setSelection(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              {selection.items.map((it, i) => {
                if (it.kind === "block") {
                  return (
                    <div key={i} className="p-3 border" style={{ borderColor: "var(--brand-border)" }}>
                      <div className="label-eyebrow mb-1" style={{ color: "#a3a3a3" }}>Blocaj</div>
                      <div className="text-sm">{it.ref.reason || "—"}</div>
                      <div className="text-xs mt-1" style={{ color: "var(--brand-text-secondary)" }}>{it.ref.start_date} → {it.ref.end_date}</div>
                      <button onClick={() => removeBlock(it.ref.id)} className="btn-ghost mt-2 text-xs">Sterge blocaj</button>
                    </div>
                  );
                }
                const s = STATUS_STYLES[it.status] || {};
                return (
                  <div key={i} className="p-3 border" style={{ borderColor: "var(--brand-border)" }}>
                    <div className="label-eyebrow mb-1" style={{ color: s.bg }}>{s.label}</div>
                    <div className="text-sm font-medium">{it.ref.guest_name || "—"}</div>
                    <div className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>{it.ref.guest_email} · {it.ref.guest_phone}</div>
                    <div className="text-xs mt-1" style={{ color: "var(--brand-text-secondary)" }}>{it.ref.start_date} → {it.ref.end_date}</div>
                    {it.ref.total_eur != null && <div className="text-xs mt-1">Total: €{it.ref.total_eur}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Drag-create block modal */}
      {blockModal && (
        <BlockCreateModal
          carName={(data.cars.find(c => c.id === blockModal.car_id) || {}).name}
          range={blockModal}
          onClose={() => setBlockModal(null)}
          onSubmit={(reason) => createBlock(blockModal.car_id, blockModal.start_date, blockModal.end_date, reason)}
        />
      )}
    </div>
  );
}

function BlockCreateModal({ carName, range, onClose, onSubmit }) {
  const [reason, setReason] = useState("Blocat din calendar");
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="block-create-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl">Blocheaza interval</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="mb-3 text-sm">
          <div><b>{carName}</b></div>
          <div style={{ color: "var(--brand-text-secondary)" }}>{range.start_date} → {range.end_date}</div>
        </div>
        <label className="label-eyebrow block mb-2">Motiv blocaj</label>
        <input className="brand-input mb-4" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Mentenanta / Indisponibil / Test drive" data-testid="block-reason" />
        <div className="flex gap-3">
          <button onClick={() => onSubmit(reason)} className="btn-gold-filled flex-1" data-testid="confirm-block">Blocheaza</button>
          <button onClick={onClose} className="btn-ghost">Anuleaza</button>
        </div>
      </div>
    </div>
  );
}
